import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Document } from '../../types/documents';
import { FileText, Download } from 'lucide-react';

export default function DocumentsWidget() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'documents'),
        orderBy('createdAt', 'desc'),
        limit(2)
      ),
      (snapshot) => {
        const documentData: Document[] = [];
        snapshot.forEach((doc) => {
          documentData.push({ id: doc.id, ...doc.data() } as Document);
        });
        setDocuments(documentData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading documents:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Documents</h2>
          <FileText className="w-5 h-5 text-text-secondary" />
        </div>

        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-center gap-4 p-4 rounded-lg bg-background"
            >
              <div className="w-10 h-10 bg-text-muted rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-text-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-text-muted rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary">Documents</h2>
        <FileText className="w-5 h-5 text-text-secondary" />
      </div>

      <div className="space-y-4">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center gap-4 p-4 rounded-lg bg-background hover:bg-primary-50 
                     transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>

            <div className="flex-1">
              <h3 className="text-sm font-medium text-text-primary">{doc.name}</h3>
              <p className="text-xs text-text-secondary">
                {doc.type} • {formatFileSize(doc.size)}
              </p>
            </div>

            <a
              href={doc.url}
              download={doc.name}
              className="p-2 text-text-secondary hover:text-primary-500 transition-colors"
            >
              <Download className="w-4 h-4" />
            </a>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="text-center py-4">
            <p className="text-text-secondary">No recent documents</p>
          </div>
        )}
      </div>
    </div>
  );
}